import React, { useState, useEffect } from 'react';
import { Cookie, Shield, Eye, Settings, Check, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  getConsentPreferences,
  updateConsentPreferences,
  getConsentDate,
  type ConsentPreferences
} from '../utils/consent';

export const CookieSettings: React.FC = () => {
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  });
  const [loading, setSaving] = useState(false);
  const [consentDate, setConsentDate] = useState<Date | null>(null);

  useEffect(() => {
    // Load current preferences
    const currentPreferences = getConsentPreferences();
    if (currentPreferences) {
      setPreferences(currentPreferences);
    }

    // Load consent date
    const date = getConsentDate();
    setConsentDate(date);
  }, []);

  const updatePreference = (key: keyof ConsentPreferences, value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      updateConsentPreferences(preferences);
      setConsentDate(new Date());
      toast.success('Cookie preferences updated successfully!');
    } catch (error) {
      toast.error('Failed to update preferences. Please try again.');
      console.error('Failed to update cookie preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    setPreferences(allAccepted);
  };

  const handleEssentialOnly = () => {
    const essentialOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
    setPreferences(essentialOnly);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <Cookie size={24} className="text-purple-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
          <p className="text-gray-600 text-sm">
            Manage how we use cookies to improve your experience
          </p>
        </div>
      </div>

      {/* Current Status */}
      {consentDate && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2">
            <Check size={16} className="text-green-600" />
            <p className="text-green-800 text-sm font-medium">
              Last updated: {consentDate.toLocaleDateString()} at {consentDate.toLocaleTimeString()}
            </p>
          </div>
        </div>
      )}

      {/* Cookie Categories */}
      <div className="space-y-6 mb-8">
        {/* Necessary Cookies */}
        <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <Shield size={20} className="text-green-600" />
              <h3 className="font-semibold text-gray-900">Necessary Cookies</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Essential for the website to function properly. These cannot be disabled.
            </p>
            <div className="text-xs text-gray-500">
              <strong>Purpose:</strong> Authentication, security, basic functionality, session management
            </div>
          </div>
          <div className="ml-4">
            <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </div>
            <p className="text-xs text-green-600 mt-1 text-center">Required</p>
          </div>
        </div>

        {/* Analytics Cookies */}
        <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <Eye size={20} className="text-blue-600" />
              <h3 className="font-semibold text-gray-900">Analytics Cookies</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Help us understand how you use our site to improve your experience.
            </p>
            <div className="text-xs text-gray-500">
              <strong>Used for:</strong> Google Analytics, usage statistics, performance monitoring, error tracking
            </div>
          </div>
          <div className="ml-4">
            <button
              onClick={() => updatePreference('analytics', !preferences.analytics)}
              className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                preferences.analytics ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </button>
            <p className="text-xs text-gray-500 mt-1 text-center">Optional</p>
          </div>
        </div>

        {/* Marketing Cookies */}
        <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <Settings size={20} className="text-purple-600" />
              <h3 className="font-semibold text-gray-900">Marketing Cookies</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Used to show you relevant advertisements and measure campaign effectiveness.
            </p>
            <div className="text-xs text-gray-500">
              <strong>Used for:</strong> Ad targeting, social media integration, promotional tracking, A/B testing
            </div>
          </div>
          <div className="ml-4">
            <button
              onClick={() => updatePreference('marketing', !preferences.marketing)}
              className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                preferences.marketing ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </button>
            <p className="text-xs text-gray-500 mt-1 text-center">Optional</p>
          </div>
        </div>

        {/* Personalization Cookies */}
        <div className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <Check size={20} className="text-pink-600" />
              <h3 className="font-semibold text-gray-900">Personalization Cookies</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Remember your preferences and provide personalized content recommendations.
            </p>
            <div className="text-xs text-gray-500">
              <strong>Used for:</strong> Language settings, theme preferences, personalized recommendations, user experience optimization
            </div>
          </div>
          <div className="ml-4">
            <button
              onClick={() => updatePreference('personalization', !preferences.personalization)}
              className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors duration-200 ${
                preferences.personalization ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
              }`}
            >
              <div className="w-4 h-4 bg-white rounded-full"></div>
            </button>
            <p className="text-xs text-gray-500 mt-1 text-center">Optional</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="border-t pt-6">
        <h4 className="font-semibold text-gray-900 mb-4">Quick Actions</h4>
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <button
            onClick={handleAcceptAll}
            className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
          >
            Accept All Cookies
          </button>
          <button
            onClick={handleEssentialOnly}
            className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition-colors duration-200"
          >
            Essential Only
          </button>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={loading}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
          ) : (
            <>
              <Save size={18} />
              <span>Save Preferences</span>
            </>
          )}
        </button>
      </div>

      {/* Legal Notice */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800 text-sm">
          <strong>Your Privacy Matters:</strong> You can change these preferences at any time. 
          Changes will take effect immediately and may require a page refresh for some features. 
          For more information about how we use your data, please see our Privacy Policy.
        </p>
      </div>
    </div>
  );
}; 